import { ApplicationResource } from '../../../common/application/enumeration/ApplicationResource';
import { sharedState } from '../../framework/state';
import { QueryParameters } from '../../../common/application/model/QueryParameters';
import { PageSize } from '../../../common/application/model/enums/PageSize';
import { SortOrder } from '../../../common/framework/model/SortOrder';
import { VehicleState } from '../../../common/application/model/enums/VehicleState';
import { P_SORT_FIELD, P_SORT_FIELDS, P_SORT_ORDER, P_SORT_ORDERS } from '../../../common/framework/constants';

export default class ParameterService {
    public parameters: Map<string, any>;
    private readonly resourceType: ApplicationResource;

    constructor(resourceType: ApplicationResource) {
        this.resourceType = resourceType;
        this.parameters = new Map();
        const parameterObject = JSON.parse(localStorage.getItem(this.resourceType) || '{}');
        if (!Object.keys(parameterObject).length) {
            const defaultParameters = this.getDefault();
            this.set(defaultParameters);
        } else {
            for (const key of Object.keys(parameterObject)) {
                this.parameters.set(key, parameterObject[key]);
            }
        }
    }

    public get(): Record<string, any> {
        return Object.fromEntries(this.parameters);
    }

    public getBackendParameters(): Map<string, any> {
        return new Map<string, any>(this.parameters);
    }

    public set(updatedParameters: Record<string, any>): void {
        const oldParameters = JSON.parse(localStorage.getItem(this.resourceType) || '{}');

        for (const oldParameter of Object.keys(oldParameters)) {
            const newKeys = Object.keys(updatedParameters);

            if (newKeys.indexOf(oldParameter) === -1) {
                this.parameters.delete(oldParameter);
            }
        }

        for (const parameter of Object.keys(updatedParameters)) {
            if (updatedParameters[parameter] || updatedParameters[parameter] === false) {
                this.parameters.set(parameter, updatedParameters[parameter]);
            } else {
                this.parameters.delete(parameter);
            }
        }

        if (!sharedState.admin && (!updatedParameters.customerId || !updatedParameters.customerId.length)) {
            if (
                sharedState.context.scopes.manager &&
                sharedState.context.scopes.manager.customerId &&
                sharedState.context.scopes.manager.customerId.length !== 0
            ) {
                const key = this.resourceType === ApplicationResource.CUSTOMER ? 'id' : 'customerId';
                this.parameters.set(key, sharedState.context.scopes.manager.customerId);
            }
        }
        this.save();
    }

    public save(): void {
        const saveAbleParameters: any = {};
        this.parameters.forEach((value, key) => {
            if (value !== undefined) {
                saveAbleParameters[key] = value;
            }
        });

        localStorage.setItem(this.resourceType, JSON.stringify(saveAbleParameters));
    }

    public getDefault(): Record<string, any> {
        switch (this.resourceType) {
            case ApplicationResource.VEHICLE_CONTRACT:
                return { ...defaultVehicleContractParams };
            case ApplicationResource.VENDOR_CONTRACT:
            case ApplicationResource.CUSTOMER_VENDOR_CONTRACT:
                return { ...defaultVendorContractParams };
            case ApplicationResource.PURCHASE_INVOICE:
            case ApplicationResource.SALES_INVOICE:
                return { ...defaultInvoiceParams };
            case ApplicationResource.SALES_INVOICE_ROW:
            case ApplicationResource.PURCHASE_INVOICE_ROW:
                return { ...defaultInvoiceRowParams };
            case ApplicationResource.VEHICLE_CONTRACT_COMMENT:
                return { ...defaultVehicleContractCommentParams };
            case ApplicationResource.FILE_META:
                return { ...defaultFileParams };
            case ApplicationResource.VENDOR:
                return { ...defaultVendorParams };
            case ApplicationResource.CUSTOMER:
                return { ...defaultCustomerParams };
            case ApplicationResource.VEHICLE_COST_MONTHLY:
                return { ...defaultDashboardParams };
            case ApplicationResource.VEHICLE_COST_MONTHLY_ROW:
                return { ...defaultVehicleCostMonthlyRowParams };
            case ApplicationResource.NETWHEELS_QUERY:
                return { ...defaultNetwheelsQueryParams };
            case ApplicationResource.VEHICLE_USAGE:
                return { ...defaultVehicleUsageQueryParams };
            case ApplicationResource.VEHICLE_COST_MONTHLY_REPORT:
                return { ...defaultVehicleCostMonthlyReportQueryParams };
            case ApplicationResource.ACCOUNT:
                return { ...defaultAccountParams };
            case ApplicationResource.PURCHASE_INVOICE_ROW_MAPPING:
                return { ...defaultPurchaseInvoiceRowMappingParams };
            case ApplicationResource.VENDOR_PURCHASE_INVOICE:
                return { ...defaultVendorPurchaseInvoiceParams };
            case ApplicationResource.REPORT:
                return { ...defaultReportParams };
            case ApplicationResource.SMART_FLEET_TASK:
                return { ...defaultSmartFleetTaskParams };
            case ApplicationResource.NOTIFICATION_RECIPIENT:
                return { ...defaultNotificationRecipientParams };
            default:
                return {
                    sortField: 'name',
                    sortOrder: SortOrder.ASC,
                    page: 1,
                    pageSize: PageSize.L,
                };
        }
    }
}

export function getAutoCompleteFieldParams(key: string, parameters: any, keyToSet?: string): Map<string, string> {
    const params = new Map();

    if (parameters[key]) {
        if (keyToSet) {
            params.set(keyToSet, parameters[key]);
        } else {
            params.set(key, parameters[key]);
        }
    }

    return params;
}

export function idListToQueryString(ids: string[]): string {
    return `${ids.map((id) => `"${id}"`).join(',')}`;
}

export const defaultVehicleContractParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    sortField: 'vehicleState',
    sortOrder: SortOrder.ASC,
    vehicleState: [
        VehicleState.ACTIVE,
        VehicleState.ORDER,
        VehicleState.DRAFT,
        VehicleState.ENDING,
        VehicleState.HANDOVER_DONE,
        VehicleState.OFFER,
        VehicleState.PENDING_APPROVAL,
        VehicleState.REALIZATION,
        VehicleState.SALE,
    ],
};

export const defaultAccountParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'code',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultVendorContractParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'name',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultInvoiceParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'entryDate',
    [P_SORT_ORDER]: SortOrder.DESC,
};

export const defaultInvoiceRowParams = {
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'rowIndex',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultVehicleContractCommentParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'created',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultFileParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'created',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultVendorParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'name',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultCustomerParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'name',
    [P_SORT_ORDER]: SortOrder.ASC,
};

export const defaultVehicleCostMonthlyRowParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'created',
    [P_SORT_ORDER]: SortOrder.DESC,
};

export const defaultNetwheelsQueryParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'created',
    [P_SORT_ORDER]: SortOrder.DESC,
};

export const defaultVehicleUsageQueryParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'created',
    [P_SORT_ORDER]: SortOrder.DESC,
};

export const defaultVehicleCostMonthlyReportQueryParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'created',
    [P_SORT_ORDER]: SortOrder.DESC,
};

export const defaultPurchaseInvoiceRowMappingParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELDS]: 'priority,vendorType',
    [P_SORT_ORDERS]: `${SortOrder.ASC},${SortOrder.ASC}`,
};

export const defaultDashboardParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
};

export const defaultReportParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
};

export const defaultVendorPurchaseInvoiceParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
};

export const defaultSmartFleetTaskParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'deadline',
    completed: false,
    adminOnly: sharedState.admin
};

export const defaultNotificationRecipientParams: QueryParameters = {
    page: 1,
    pageSize: PageSize.L,
    [P_SORT_FIELD]: 'email',
    [P_SORT_ORDER]: SortOrder.ASC,
};
